import {NormalizedCacheObject} from "apollo-cache-inmemory"
import ApolloClient from "apollo-client"
import {setCachedJwtToken} from "../client"
import {SecureStoreKeys} from "../constants"
import {LoginDocument, LoginMutation, LoginMutationVariables} from "../graphql"
import {decode, tokenExpired} from "./jwt"
import * as SecureStore from "./secureStore"

export const login = async ({
    id,
    jwtToken,
    refreshToken,
}: {
    id?: string
    jwtToken?: string
    refreshToken?: string
}) => {
    if (jwtToken) {
        setCachedJwtToken(jwtToken)
    }

    await Promise.all([
        id
            ? SecureStore.setItemAsync(SecureStoreKeys.id, id)
            : Promise.resolve(),
        jwtToken
            ? SecureStore.setItemAsync(SecureStoreKeys.jwtToken, jwtToken)
            : Promise.resolve(),
        refreshToken
            ? SecureStore.setItemAsync(
                  SecureStoreKeys.refreshToken,
                  refreshToken,
              )
            : Promise.resolve(),
    ])
}

export const getMyUserId = async () =>
    (await SecureStore.getItemAsync(SecureStoreKeys.id)) ?? undefined

export const getMyRefreshToken = async () =>
    (await SecureStore.getItemAsync(SecureStoreKeys.refreshToken)) ?? undefined

export const logout = async (updateCache = true) => {
    if (updateCache) {
        setCachedJwtToken("")
    }

    await Promise.all([
        SecureStore.deleteItemAsync(SecureStoreKeys.id),
        SecureStore.deleteItemAsync(SecureStoreKeys.jwtToken),
        SecureStore.deleteItemAsync(SecureStoreKeys.refreshToken),
    ])
}

export const getNewToken = async (
    client: ApolloClient<NormalizedCacheObject>,
) => {
    const [id, token] = await Promise.all([
        SecureStore.getItemAsync(SecureStoreKeys.id),
        SecureStore.getItemAsync(SecureStoreKeys.refreshToken),
    ])
    if (!id || !token) {
        await logout(false)
        return ""
    }

    const {data, errors} = await client.mutate<
        LoginMutation,
        LoginMutationVariables
    >({
        mutation: LoginDocument,
        variables: {id, token},
    })
    if (errors || !data || !data.login) {
        await logout(false)
        return ""
    }

    await SecureStore.setItemAsync(SecureStoreKeys.jwtToken, data.login)
    return data.login
}

export const getJwtToken = async (
    client: ApolloClient<NormalizedCacheObject>,
) => {
    const jwtToken = await SecureStore.getItemAsync(SecureStoreKeys.jwtToken)
    if (!jwtToken || tokenExpired(jwtToken)) {
        return await getNewToken(client)
    }
    return jwtToken
}

export const getAccountStatus = (
    jwtToken: string,
): "Verified" | "NotVerified" | undefined => {
    try {
        return decode(jwtToken)?.accountStatus
    } catch {
        return undefined
    }
}
